import { useState } from 'react';
// Pure: the page's words, read out of its markup and written back in place. The phone's own.
import { copyContextLabel, writeCopy, type CopyItem } from '@/webapp/content';
// The phone's words for a wording change, kept for Undo last change.
import { manageTextChange } from '@/webapp/chatEdit';
import { Button, Icon, TextField } from '../../../ui';

/** A phrase this long reads better in a box than on one line. */
const LONG = 60;

/**
 * The words on the page: headings, button labels, the intro line, the empty state — the phone's
 * Text tab (src/webapp/WebAppAdminPanel.tsx).
 *
 * Apart from Content because it is a different kind of edit: Content changes what the site is
 * about, this changes what it says. Only static markup is offered; words the app builds in code
 * are computed, and "fixing" one would be undone a moment later (see readCopy).
 *
 * A phrase is addressed by its place in the list read from `source`, and `writeCopy` refuses if
 * that text has no such place any more rather than landing on whichever phrase sits there now.
 */
export default function TextSection({
  copy,
  source,
  locked,
  onSaveSource,
}: {
  copy: CopyItem[];
  source: string;
  locked: boolean;
  /** Saves and publishes; `what` is the change in words, for Undo. Rejects only when nothing was saved. */
  onSaveSource: (next: string, what: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(): Promise<void> {
    if (editing === null || saving || locked) return;
    setError(null);
    setSaving(true);
    try {
      const was = copy[editing]?.text ?? '';
      // The same words back: nothing to save, and nothing to republish.
      if (draft.trim() === was) {
        setEditing(null);
        return;
      }
      // Read back by Undo as "…before “Change Welcome to Hello”".
      await onSaveSource(writeCopy(source, editing, draft), manageTextChange(was, draft));
      setEditing(null);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Please try again.');
    } finally {
      setSaving(false);
    }
  }

  if (editing !== null) {
    const item = copy[editing];
    return (
      <div className="manage-edit">
        <h3 className="type-heading">Edit text</h3>
        <TextField
          label={item ? copyContextLabel(item.context) : 'Text'}
          value={draft}
          onChangeText={setDraft}
          multiline={(item?.text.length ?? 0) > LONG}
          hint="The wording shown on the page."
          autoFocus
        />
        {error && (
          <p role="alert" className="type-caption field-error-text">
            <strong>Couldn&apos;t save.</strong> {error}
          </p>
        )}
        <div className="build-actions">
          <Button
            title="Save and republish"
            loading={saving}
            disabled={locked}
            accessibilityLabel="Save this wording and update the live app"
            onPress={() => void save()}
          />
          <Button title="Cancel" variant="ghost" accessibilityLabel="Cancel editing" onPress={() => setEditing(null)} />
        </div>
      </div>
    );
  }

  return (
    <ul className="project-list manage-list">
      {copy.map((item, i) => (
        <li key={`${item.start}-${i}`}>
          <button
            type="button"
            className="card clay-card project-row pressable"
            aria-label={`Edit the text "${item.text}"`}
            onClick={() => {
              setDraft(item.text);
              setError(null);
              setEditing(i);
            }}
          >
            <span className="project-text">
              <span className="type-body">{item.text}</span>
              <span className="type-caption project-meta">{copyContextLabel(item.context)}</span>
            </span>
            <Icon name="chevron" size={18} color="var(--color-chevron)" />
          </button>
        </li>
      ))}
    </ul>
  );
}
